import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useSubscriptionGuard from '../hooks/useSubscriptionGuard';
import { buildUrl, getIntegrationStatus, getProviderAuthUrl, disconnectProvider, deleteAccount } from '../services/api';

function FamilyManager() {
    const [email, setEmail] = useState('');
    const [inviteLink, setInviteLink] = useState('');
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem('access_token');

    // Prefer full name; fallback to email, then user_id
    const getMemberDisplayName = (member) => {
        if (!member) return '';
        const fullName = member.full_name
            || [member.first_name || member.given_name, member.last_name || member.family_name]
                .filter(Boolean)
                .join(' ')
            || member.name;
        return fullName && fullName.trim()
            ? fullName
            : (member.email || member.user_id || '');
    };

    const fetchMemberProfile = async (userId) => {
        try {
            const res = await fetch(buildUrl(`/users/${encodeURIComponent(userId)}`), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                return await res.json();
            }
        } catch (_) {}
        return null;
    };

    const loadMembers = async () => {
        try {
            const res = await fetch(buildUrl('/family/members'), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const baseMembers = Array.isArray(data.members) ? data.members : [];
                // Enrich with profile names if missing
                const enriched = await Promise.all(baseMembers.map(async (m) => {
                    if (m && (m.full_name || m.first_name || m.last_name || m.email)) {
                        return m;
                    }
                    const profile = await fetchMemberProfile(m.user_id);
                    if (profile) {
                        const firstName = profile.first_name || profile.given_name || null;
                        const lastName = profile.last_name || profile.family_name || null;
                        const fullName = profile.full_name || profile.name || [firstName, lastName].filter(Boolean).join(' ');
                        return {
                            ...m,
                            first_name: firstName || undefined,
                            last_name: lastName || undefined,
                            full_name: fullName || undefined,
                            email: profile.email || m.email || undefined,
                        };
                    }
                    return m;
                }));
                setMembers(enriched);
            }
        } catch (e) {
            console.error('Failed to load members', e);
        }
    };

    useEffect(() => { loadMembers(); }, []);

    const sendInvite = async (e) => {
        e.preventDefault();
        setInviteLink('');
        setLoading(true);
        try {
            const res = await fetch(buildUrl('/family/invites'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
            });
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || 'Failed to create invite');
            }
            const data = await res.json();
            setInviteLink(data.invite_link);
            setEmail('');
        } catch (e) {
            alert('Invite failed. Ensure you are the family owner.');
        } finally {
            setLoading(false);
        }
    };

    const removeMember = async (userId) => {
        if (!window.confirm('Remove this member?')) return;
        try {
            const res = await fetch(buildUrl(`/family/members/${userId}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                loadMembers();
            }
        } catch (e) {
            console.error('Remove failed', e);
        }
    };

    return (
        <div>
            <form onSubmit={sendInvite} className="flex items-center space-x-2 mb-4">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="family@email.com"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                    required
                />
                <button type="submit" disabled={loading} className="px-4 py-2 bg-red-400 hover:bg-red-300 text-white rounded">
                    {loading ? 'Sending...' : 'Send Invite'}
                </button>
            </form>
            {inviteLink && (
                <div className="p-3 bg-green-50 border border-green-200 rounded text-sm">
                    Invite link: <a className="text-green-700 underline" href={inviteLink} target="_blank" rel="noreferrer">{inviteLink}</a>
                </div>
            )}
            <div className="mt-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Members</h4>
                <ul className="space-y-2">
                    {members.map(m => (
                        <li key={m.SK || m.user_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                            <span className="text-sm text-gray-800 dark:text-gray-200">{getMemberDisplayName(m)} ({m.role})</span>
                            <button onClick={() => removeMember(m.user_id)} className="text-sm text-red-600 dark:text-red-400">Remove</button>
                        </li>
                    ))}
                    {!members.length && (
                        <li className="text-sm text-gray-500 dark:text-gray-400">No members yet</li>
                    )}
                </ul>
            </div>
        </div>
    );
}

function BillingHistory() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const run = async () => {
            try {
                const token = localStorage.getItem('access_token');
                const res = await fetch(buildUrl('/subscription/invoices'), { headers: { 'Authorization': `Bearer ${token}` } });
                if (res.ok) {
                    const data = await res.json();
                    setItems(Array.isArray(data.invoices) ? data.invoices : []);
                }
            } catch (e) {
                console.error('Invoices load failed', e);
            } finally {
                setLoading(false);
            }
        };
        run();
    }, []);

    if (loading) return <p className="text-gray-500 dark:text-gray-400">Loading...</p>;
    if (!items.length) return <p className="text-gray-500 dark:text-gray-400">No billing history available.</p>;
    return (
        <div className="space-y-3">
            {items.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white">{new Date(inv.created * 1000).toLocaleDateString()}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{(inv.billing_reason || 'Subscription').replace(/_/g,' ')}</p>
                    </div>
                    <div className="text-right">
                        <p className="font-medium text-gray-900 dark:text-white">${inv.amount_paid.toFixed(2)}</p>
                        {(inv.invoice_pdf || inv.hosted_invoice_url) && (
                            <a href={inv.invoice_pdf || inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300">Download Invoice</a>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function Settings() {
    const navigate = useNavigate();
    
    // Subscription guard - redirect if no active subscription
    const { isSubscriptionValid, isLoading: subscriptionGuardLoading } = useSubscriptionGuard();
    
    const [isDarkMode, setIsDarkMode] = useState(() => {
        if (typeof window !== 'undefined') {
            const savedTheme = localStorage.getItem('theme');
            return savedTheme === 'dark';
        }
        return false;
    });
    
    // User profile state
    const [user, setUser] = useState(null);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [loading, setLoading] = useState(true);
    
    // Settings form states
    const [activeTab, setActiveTab] = useState('profile');
    const [integrations, setIntegrations] = useState({ google_drive: false, dropbox: false, onedrive: false });
    const [integrationsLoading, setIntegrationsLoading] = useState(false);
    const [profileData, setProfileData] = useState({
        fullName: '',
        email: '',
        phone: '',
        timezone: 'UTC',
        language: 'en'
    });
    
    const [securityData, setSecurityData] = useState({
        twoFactorEnabled: false,
        emailNotifications: true,
        smsNotifications: false
    });
    
    const [notificationData, setNotificationData] = useState({
        emailUpdates: true,
        pushNotifications: true,
        weeklyDigest: true,
        marketingEmails: false
    });
    
    const [subscriptionData, setSubscriptionData] = useState({
        plan: 'No Plan',
        status: 'Inactive',
        nextBilling: '2024-02-15',
        storageUsed: '2.5 GB',
        storageLimit: '1 GB'
    });
    
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);

    // Fetch user profile data
    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const token = localStorage.getItem('access_token');
                if (!token) {
                    navigate('/login');
                    return;
                }

                const response = await fetch(buildUrl('/me'), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const userData = await response.json();
                    setUser(userData);
                    setProfileData({
                        fullName: userData.full_name || '',
                        email: userData.email || '',
                        phone: userData.phone || '',
                        timezone: userData.timezone || 'UTC',
                        language: userData.language || 'en'
                    });
                } else {
                    localStorage.removeItem('access_token');
                    navigate('/login');
                }
            } catch (error) {
                console.error('Failed to fetch user profile:', error);
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };

        fetchUserProfile();
    }, [navigate]);

    // Load integration status when entering settings or switching to Integrations tab
    useEffect(() => {
        const load = async () => {
            try {
                setIntegrationsLoading(true);
                const st = await getIntegrationStatus();
                setIntegrations(st || { google_drive: false, dropbox: false, onedrive: false });
            } catch (e) {
                // ignore
            } finally {
                setIntegrationsLoading(false);
            }
        };
        load();
    }, [activeTab]);

    // Fetch subscription data
    useEffect(() => {
        const fetchSubscriptionData = async () => {
            try {
                const token = localStorage.getItem('access_token');
                if (!token) return;

                const response = await fetch(buildUrl('/subscription/'), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const subData = await response.json();
                    console.log('📊 Subscription Data:', subData); // Debug log
                    setSubscriptionData({
                        plan: subData.plan_type === 'individual' ? 'Individual' : 
                              subData.plan_type === 'family' ? 'Family' : 'No Plan',
                        status: subData.subscription_status === 'active' ? 'Active' : 
                               subData.subscription_status === 'cancelled' ? 'Cancelled' : 'Inactive',
                        nextBilling: subData.current_period_end ? 
                                   new Date(subData.current_period_end).toLocaleDateString() : 'N/A',
                        billingCycle: subData.billing_cycle || 'month',
                        storageUsed: subData.storage_used || '—',
                        storageLimit: null,
                        stripeSubscriptionId: subData.stripe_subscription_id,
                        familyOrgId: subData.family_org_id,
                        familySeatsLimit: subData.family_seats_limit,
                        familySeatsUsed: subData.family_seats_used,
                    });
                }
            } catch (error) {
                console.error('Failed to fetch subscription data:', error);
            }
        };

        if (user) {
            fetchSubscriptionData();
        }
    }, [user]);

    // Initialize theme
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        if (!savedTheme) {
            setIsDarkMode(false);
        } else {
            setIsDarkMode(savedTheme === 'dark');
        }
        
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        if (savedTheme === 'dark') {
            htmlElement.classList.add('dark');
            bodyElement.classList.add('dark');
        } else {
            htmlElement.classList.remove('dark');
            bodyElement.classList.remove('dark');
        }
    }, []);

    // Apply theme changes
    useEffect(() => {
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        if (isDarkMode) {
            htmlElement.classList.add('dark');
            bodyElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        } else {
            htmlElement.classList.remove('dark');
            bodyElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    }, [isDarkMode]);

    const toggleDarkMode = () => {
        setIsDarkMode(!isDarkMode);
    };

    // Profile dropdown handlers
    const toggleProfileDropdown = () => {
        setShowProfileDropdown(!showProfileDropdown);
    };

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        navigate('/login');
    };

    const goToDashboard = () => {
        navigate('/dashboard');
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showProfileDropdown && !event.target.closest('.profile-dropdown-container')) {
                setShowProfileDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showProfileDropdown]);

    // Generate initials for email users
    const getInitials = (name, email) => {
        if (name && name.trim()) {
            return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
        }
        return email ? email[0].toUpperCase() : 'U';
    };

    // Form handlers
    const handleProfileSave = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(buildUrl('/me'), {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(profileData)
            });

            if (response.ok) {
                alert('Profile updated successfully!');
            } else {
                alert('Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('Error updating profile');
        }
    };

    const handleSubscriptionUpgrade = () => {
        // Navigate to plan selection page
        navigate('/plan-selection');
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
            return;
        }

        setSubscriptionLoading(true);
        
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(buildUrl('/subscription/cancel'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                // Update local state
                setSubscriptionData(prev => ({
                    ...prev,
                    status: 'Cancelled'
                }));
                alert('Subscription cancelled successfully. You will continue to have access until the end of your current billing period.');
            } else {
                const errorData = await response.json();
                alert(`Failed to cancel subscription: ${errorData.detail || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            alert('Error cancelling subscription. Please try again.');
        } finally {
            setSubscriptionLoading(false);
        }
    };

    const handleManageBilling = async () => {
        setSubscriptionLoading(true);
        
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(buildUrl('/subscription/customer-portal'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                // Redirect to Stripe Customer Portal
                window.open(data.portal_url, '_blank');
            } else {
                alert('Failed to open billing portal. Please try again.');
            }
        } catch (error) {
            console.error('Error opening billing portal:', error);
            alert('Error opening billing portal. Please try again.');
        } finally {
            setSubscriptionLoading(false);
        }
    };

    // Account deletion modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteAtPeriodEnd, setDeleteAtPeriodEnd] = useState(true);
    const [hardDelete, setHardDelete] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');

    // Change Plan modal state
    const [showChangePlan, setShowChangePlan] = useState(false);
    const [changePlanType, setChangePlanType] = useState('individual'); // 'individual' | 'family'
    const [changeBillingCycle, setChangeBillingCycle] = useState('month'); // 'month' | 'year'
    const [changePlanLoading, setChangePlanLoading] = useState(false);
    const [changePlanError, setChangePlanError] = useState('');

    const openChangePlanModal = () => {
        const currentPlanType = (subscriptionData.plan || 'Individual').toLowerCase();
        const currentCycle = (subscriptionData.billingCycle || 'month').toLowerCase();
        setChangePlanType(currentPlanType === 'family' ? 'family' : 'individual');
        setChangeBillingCycle(currentCycle === 'year' ? 'year' : 'month');
        setChangePlanError('');
        setShowChangePlan(true);
    };

    const submitChangePlan = async () => {
        try {
            setChangePlanLoading(true);
            setChangePlanError('');
            const token = localStorage.getItem('access_token');
            const resp = await fetch(buildUrl('/subscription/change-plan'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    plan_type: changePlanType,
                    billing_cycle: changeBillingCycle
                })
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to change plan');
            }
            const data = await resp.json();
            // Update local subscription view
            setSubscriptionData(prev => ({
                ...prev,
                plan: data.plan_type === 'family' ? 'Family' : 'Individual',
                billingCycle: data.billing_cycle || prev.billingCycle
            }));
            setShowChangePlan(false);
            alert('Plan updated successfully. Prorations may apply.');
        } catch (e) {
            setChangePlanError(e.message || 'Failed to change plan');
        } finally {
            setChangePlanLoading(false);
        }
    };

    const confirmDeleteAccount = async () => {
        if (!window.confirm('Final confirmation: proceed with account deletion?')) return;
        try {
            setDeleteLoading(true);
            await deleteAccount({ cancelAtPeriodEnd: deleteAtPeriodEnd, reason: deleteReason || null, hardDelete });
            // Clear auth and redirect
            localStorage.removeItem('access_token');
            window.location.href = '/';
        } catch (e) {
            alert(e.message || 'Failed to delete account');
        } finally {
            setDeleteLoading(false);
        }
    };

    // Loading state - wait for both user and subscription guard
    if (loading || subscriptionGuardLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-400 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">
                        {subscriptionGuardLoading ? 'Checking subscription...' : 'Loading...'}
                    </p>
                </div>
            </div>
        );
    }

    // If subscription guard hasn't validated, don't render (will redirect)
    if (!isSubscriptionValid) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={goToDashboard}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded transition duration-200"
                            >
                                <i className="fas fa-arrow-left mr-2"></i>
                                Back to Dashboard
                            </button>
                            <h1 className="text-2xl font-semibold font-heading text-gray-900 dark:text-white">Settings</h1>
                        </div>
                        
                        {/* Right side controls */}
                        <div className="flex items-center space-x-4">
                            {/* Dark mode toggle */}
                            <button 
                                onClick={toggleDarkMode}
                                className="dark-mode-toggle inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded transition duration-200"
                            >
                                <span className="dark-mode-text">
                                    {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                                </span>
                            </button>
                            
                            {/* User Profile Dropdown */}
                            <div className="relative profile-dropdown-container">
                                <button
                                    onClick={toggleProfileDropdown}
                                    className="flex items-center space-x-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
                                >
                                    {/* Profile Avatar */}
                                    <div className="relative">
                                        {user?.google_id ? (
                                            user?.profile_picture ? (
                                                <img 
                                                    src={user.profile_picture} 
                                                    alt="Profile" 
                                                    className="w-10 h-10 rounded-full shadow-lg object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null
                                        ) : null}
                                        <div 
                                            className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-lg ${
                                                user?.google_id 
                                                    ? 'bg-gradient-to-br from-pink-500 to-red-500' 
                                                    : 'bg-gradient-to-br from-gray-500 to-gray-600'
                                            } ${user?.profile_picture ? 'hidden' : ''}`}
                                        >
                                            {user?.google_id ? (
                                                getInitials(user.full_name, user.email)
                                            ) : (
                                                <i className="fas fa-user text-sm"></i>
                                            )}
                                        </div>
                                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-white dark:border-gray-800 rounded-full"></div>
                                    </div>
                                </button>

                                {/* Dropdown Menu */}
                                {showProfileDropdown && (
                                    <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                            <div className="flex items-center space-x-3">
                                                <div className="flex-shrink-0 relative">
                                                    {user?.google_id && user?.profile_picture ? (
                                                        <img 
                                                            src={user.profile_picture} 
                                                            alt="Profile" 
                                                            className="w-12 h-12 rounded-full shadow-lg object-cover"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div 
                                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold shadow-lg ${
                                                            user?.google_id 
                                                                ? 'bg-gradient-to-br from-pink-500 to-red-500' 
                                                                : 'bg-gradient-to-br from-gray-500 to-gray-600'
                                                        } ${user?.profile_picture ? 'hidden' : ''}`}
                                                    >
                                                        {user?.google_id ? (
                                                            getInitials(user.full_name, user.email)
                                                        ) : (
                                                            <i className="fas fa-user"></i>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                        {user?.full_name || 'User'}
                                                    </p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                        {user?.email}
                                                    </p>
                                                    <div className="flex items-center mt-1">
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                            <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                                                            {user?.google_id ? 'Google Account' : 'Email Account'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="py-1">
                                            <button
                                                onClick={goToDashboard}
                                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
                                            >
                                                <i className="fas fa-tachometer-alt mr-3 text-gray-400"></i>
                                                Dashboard
                                            </button>
                                            <button
                                                onClick={handleLogout}
                                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition duration-200"
                                            >
                                                <i className="fas fa-sign-out-alt mr-3"></i>
                                                Logout
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Settings Content */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                    <div className="flex">
                        {/* Sidebar Navigation */}
                        <div className="w-64 bg-gray-50 dark:bg-gray-700 border-r border-gray-200 dark:border-gray-600">
                            <nav className="p-4">
                                <ul className="space-y-2">
                                    <li>
                                        <button
                                            onClick={() => setActiveTab('profile')}
                                            className={`w-full text-left flex items-center px-4 py-3 rounded-lg transition duration-200 ${
                                                activeTab === 'profile'
                                                    ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            <i className="fas fa-user mr-3"></i>
                                            Profile
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => setActiveTab('security')}
                                            className={`w-full text-left flex items-center px-4 py-3 rounded-lg transition duration-200 ${
                                                activeTab === 'security'
                                                    ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            <i className="fas fa-shield-alt mr-3"></i>
                                            Security
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => setActiveTab('notifications')}
                                            className={`w-full text-left flex items-center px-4 py-3 rounded-lg transition duration-200 ${
                                                activeTab === 'notifications'
                                                    ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            <i className="fas fa-bell mr-3"></i>
                                            Notifications
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => setActiveTab('subscription')}
                                            className={`w-full text-left flex items-center px-4 py-3 rounded-lg transition duration-200 ${
                                                activeTab === 'subscription'
                                                    ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            <i className="fas fa-credit-card mr-3"></i>
                                            Subscription
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => setActiveTab('preferences')}
                                            className={`w-full text-left flex items-center px-4 py-3 rounded-lg transition duration-200 ${
                                                activeTab === 'preferences'
                                                    ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            <i className="fas fa-cog mr-3"></i>
                                            Preferences
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => setActiveTab('integrations')}
                                            className={`w-full text-left flex items-center px-4 py-3 rounded-lg transition duration-200 ${
                                                activeTab === 'integrations'
                                                    ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            <i className="fas fa-plug mr-3"></i>
                                            Integrations
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>

                        {/* Main Content */}
                        <div className="flex-1 p-8">
                            {/* Profile Tab */}
                            {activeTab === 'profile' && (
                                <div>
                                    <div className="mb-8">
                                        <h2 className="text-3xl font-semibold font-heading text-gray-900 dark:text-white mb-2">Profile Settings</h2>
                                        <p className="text-gray-600 dark:text-gray-400">Manage your personal information and account details.</p>
                                    </div>

                                    <div className="space-y-6">
                                        {/* Profile Picture Section */}
                                        <div className="flex items-center space-x-6">
                                            <div className="relative">
                                                {user?.google_id && user?.profile_picture ? (
                                                    <img 
                                                        src={user.profile_picture} 
                                                        alt="Profile" 
                                                        className="w-24 h-24 rounded-full shadow-lg object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center text-white font-semibold text-xl shadow-lg">
                                                        {getInitials(user?.full_name, user?.email)}
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Profile Photo</h3>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                                    {user?.google_id ? 'Synced from Google Account' : 'Upload a profile picture'}
                                                </p>
                                                {!user?.google_id && (
                                                    <button className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded transition duration-200">
                                                        <i className="fas fa-upload mr-2"></i>
                                                        Upload Photo
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Personal Information */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Full Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={profileData.fullName}
                                                    onChange={(e) => setProfileData({...profileData, fullName: e.target.value})}
                                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-400 focus:border-transparent transition duration-200"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Email Address
                                                </label>
                                                <input
                                                    type="email"
                                                    value={profileData.email}
                                                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-400 focus:border-transparent transition duration-200"
                                                    disabled={user?.google_id}
                                                />
                                                {user?.google_id && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                        Email is managed by your Google account
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Phone Number
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={profileData.phone}
                                                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-400 focus:border-transparent transition duration-200"
                                                    placeholder="+1 (555) 123-4567"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Timezone
                                                </label>
                                                <select
                                                    value={profileData.timezone}
                                                    onChange={(e) => setProfileData({...profileData, timezone: e.target.value})}
                                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-400 focus:border-transparent transition duration-200"
                                                >
                                                    <option value="UTC">UTC</option>
                                                    <option value="America/New_York">Eastern Time</option>
                                                    <option value="America/Chicago">Central Time</option>
                                                    <option value="America/Denver">Mountain Time</option>
                                                    <option value="America/Los_Angeles">Pacific Time</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="flex justify-end">
                                            <button
                                                onClick={handleProfileSave}
                                                className="inline-flex items-center px-6 py-3 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
                                            >
                                                <i className="fas fa-save mr-2"></i>
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Security Tab */}
                            {activeTab === 'security' && (
                                <div>
                                    <div className="mb-8">
                                        <h2 className="text-3xl font-semibold font-heading text-gray-900 dark:text-white mb-2">Security & Privacy</h2>
                                        <p className="text-gray-600 dark:text-gray-400">Manage your account security and privacy settings.</p>
                                    </div>

                                    <div className="space-y-8">
                                        {/* Password Section */}
                                        {!user?.google_id && (
                                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Password</h3>
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            Current Password
                                                        </label>
                                                        <input
                                                            type="password"
                                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-400 focus:border-transparent transition duration-200"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            New Password
                                                        </label>
                                                        <input
                                                            type="password"
                                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-400 focus:border-transparent transition duration-200"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            Confirm New Password
                                                        </label>
                                                        <input
                                                            type="password"
                                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-400 focus:border-transparent transition duration-200"
                                                        />
                                                    </div>
                                                    <button className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200">
                                                        Update Password
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Two-Factor Authentication */}
                                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                            <div className="flex items-center justify-between mb-4">
                                                <div>
                                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Two-Factor Authentication</h3>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Add an extra layer of security to your account</p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={securityData.twoFactorEnabled}
                                                        onChange={(e) => setSecurityData({...securityData, twoFactorEnabled: e.target.checked})}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-400"></div>
                                                </label>
                                            </div>
                                            {securityData.twoFactorEnabled && (
                                                <div className="space-y-2">
                                                    <p className="text-sm text-green-600 dark:text-green-400">
                                                        <i className="fas fa-check-circle mr-2"></i>
                                                        Two-factor authentication is enabled
                                                    </p>
                                                    <button className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 rounded transition duration-200">
                                                        View Recovery Codes
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Login Sessions */}
                                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Active Sessions</h3>
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg">
                                                    <div className="flex items-center space-x-3">
                                                        <i className="fas fa-laptop text-green-500 text-xl"></i>
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">Windows PC - Chrome</p>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400">Current session • New York, NY</p>
                                                        </div>
                                                    </div>
                                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                        Active
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg">
                                                    <div className="flex items-center space-x-3">
                                                        <i className="fas fa-mobile-alt text-blue-500 text-xl"></i>
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">iPhone - Safari</p>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400">2 hours ago • New York, NY</p>
                                                        </div>
                                                    </div>
                                                    <button className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium">
                                                        Revoke
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Notifications Tab */}
                            {activeTab === 'notifications' && (
                                <div>
                                    <div className="mb-8">
                                        <h2 className="text-3xl font-semibold font-heading text-gray-900 dark:text-white mb-2">Notifications</h2>
                                        <p className="text-gray-600 dark:text-gray-400">Manage how you receive notifications and updates.</p>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Email Notifications</h3>
                                            <div className="space-y-4">
                                                {[
                                                    { key: 'emailUpdates', label: 'Product updates and announcements', desc: 'Get notified about new features and updates' },
                                                    { key: 'weeklyDigest', label: 'Weekly activity digest', desc: 'Summary of your weekly activity and insights' },
                                                    { key: 'marketingEmails', label: 'Marketing emails', desc: 'Promotional content and special offers' }
                                                ].map((item) => (
                                                    <div key={item.key} className="flex items-center justify-between">
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">{item.label}</p>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                                                        </div>
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={notificationData[item.key]}
                                                                onChange={(e) => setNotificationData({...notificationData, [item.key]: e.target.checked})}
                                                                className="sr-only peer"
                                                            />
                                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-400"></div>
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Push Notifications</h3>
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-white">Browser notifications</p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications in your browser</p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={notificationData.pushNotifications}
                                                            onChange={(e) => setNotificationData({...notificationData, pushNotifications: e.target.checked})}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-400"></div>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Subscription Tab */}
                            {activeTab === 'subscription' && (
                                <div>
                                    <div className="mb-8">
                                        <h2 className="text-3xl font-semibold font-heading text-gray-900 dark:text-white mb-2">Subscription Management</h2>
                                        <p className="text-gray-600 dark:text-gray-400">Manage your subscription plan and billing information.</p>
                                    </div>

                                    <div className="space-y-6">
                                        {/* Current Plan */}
                                        <div className="bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-lg p-6 border border-red-200 dark:border-red-800">
                                            <div className="flex items-center justify-between mb-4">
                                                <div>
                                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{subscriptionData.plan} Plan</h3>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        Status: <span className={`font-medium ${
                                                            subscriptionData.status === 'Active' ? 'text-green-600 dark:text-green-400' :
                                                            subscriptionData.status === 'Cancelled' ? 'text-red-600 dark:text-red-400' :
                                                            'text-yellow-600 dark:text-yellow-400'
                                                        }`}>{subscriptionData.status}</span>
                                                    </p>
                                                    {subscriptionData.billingCycle && subscriptionData.plan !== 'Free' && (
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            Billed {subscriptionData.billingCycle === 'year' ? 'annually' : 'monthly'}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                        {subscriptionData.plan === 'Individual' ? (subscriptionData.billingCycle === 'year' ? '$180' : '$15') :
                                                         subscriptionData.plan === 'Family' ? (subscriptionData.billingCycle === 'year' ? '$480' : '$40') : 'No active plan'}
                                                        <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                                                            {(subscriptionData.plan === 'Individual' || subscriptionData.plan === 'Family') ? 
                                                                (subscriptionData.billingCycle === 'year' ? '/year' : '/month') : ''}
                                                        </span>
                                                    </p>
                                                    {(subscriptionData.plan === 'Student' || subscriptionData.plan === 'Individual') && subscriptionData.nextBilling !== 'N/A' && (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                                            {subscriptionData.status === 'Cancelled' ? 
                                                                `Access until: ${subscriptionData.nextBilling}` :
                                                                `Next billing: ${subscriptionData.nextBilling}`
                                                            }
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Storage Used (no limit) */}
                                            <div className="mb-4">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Storage Used</span>
                                                    <span className="text-sm text-gray-900 dark:text-gray-100">
                                                        {subscriptionData.storageUsed || '—'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex space-x-3">
                                                {/* Debug: Current subscription data */}
                                                {console.log('🔘 Current subscription data:', subscriptionData)}
                                                {(subscriptionData.plan === 'No Plan' || subscriptionData.status === 'Inactive') ? (
                                                    <button
                                                        onClick={handleSubscriptionUpgrade}
                                                        className="inline-flex items-center px-6 py-3 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
                                                    >
                                                        <i className="fas fa-arrow-up mr-2"></i>
                                                        Choose a Plan
                                                    </button>
                                                ) : subscriptionData.status === 'Cancelled' ? (
                                                    <button
                                                        onClick={handleSubscriptionUpgrade}
                                                        className="inline-flex items-center px-6 py-3 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
                                                    >
                                                        <i className="fas fa-redo mr-2"></i>
                                                        Reactivate Subscription
                                                    </button>
                                                ) : (
                                                    <>
                                                        {subscriptionData.plan === 'Family' && (
                                                            <a
                                                                href="#family-management"
                                                                className="inline-flex items-center px-6 py-3 mr-2 text-sm font-medium leading-normal bg-green-100 hover:bg-green-200 text-green-700 rounded transition duration-200"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    const el = document.getElementById('family-management');
                                                                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                                                                }}
                                                            >
                                                                <i className="fas fa-user-friends mr-2"></i>
                                                                Invite family members
                                                            </a>
                                                        )}
                                                        <button 
                                                            onClick={openChangePlanModal}
                                                            className="inline-flex items-center px-6 py-3 mr-2 text-sm font-medium leading-normal bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition duration-200"
                                                        >
                                                            <i className="fas fa-exchange-alt mr-2"></i>
                                                            Change Plan
                                                        </button>
                                                        <button 
                                                            onClick={handleManageBilling}
                                                            disabled={subscriptionLoading}
                                                            className="inline-flex items-center px-6 py-3 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded transition duration-200 disabled:opacity-50"
                                                        >
                                                            <i className="fas fa-credit-card mr-2"></i>
                                                            {subscriptionLoading ? 'Loading...' : 'Manage Billing'}
                                                        </button>
                                                        <button
                                                            onClick={handleCancelSubscription}
                                                            disabled={subscriptionLoading}
                                                            className="inline-flex items-center px-6 py-3 text-sm font-medium leading-normal bg-red-100 hover:bg-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded transition duration-200 disabled:opacity-50"
                                                        >
                                                            <i className="fas fa-times mr-2"></i>
                                                            {subscriptionLoading ? 'Cancelling...' : 'Cancel Subscription'}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Plan Comparison removed */}

                                        {/* Billing History */}
                                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Billing History</h3>
                                            <BillingHistory />
                                        </div>

                                        {/* Family Management */}
                                        {subscriptionData.plan === 'Family' && (
                                            <div id="family-management" className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Family Management</h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Invite family members and manage seats (used {subscriptionData.familySeatsUsed || 0} of {subscriptionData.familySeatsLimit || 5}).</p>

                                                <FamilyManager />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Preferences Tab */}
                            {activeTab === 'preferences' && (
                                <div>
                                    <div className="mb-8">
                                        <h2 className="text-3xl font-semibold font-heading text-gray-900 dark:text-white mb-2">Preferences</h2>
                                        <p className="text-gray-600 dark:text-gray-400">Customize your application experience and preferences.</p>
                                    </div>

                                    <div className="space-y-6">
                                        {/* Appearance */}
                                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Appearance</h3>
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-white">Dark Mode</p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">Toggle between light and dark themes</p>
                                                    </div>
                                                    <button 
                                                        onClick={toggleDarkMode}
                                                        className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 rounded transition duration-200"
                                                    >
                                                        {isDarkMode ? 'Switch to Light' : 'Switch to Dark'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Language & Region */}
                                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Language & Region</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Language
                                                    </label>
                                                    <select
                                                        value={profileData.language}
                                                        onChange={(e) => setProfileData({...profileData, language: e.target.value})}
                                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-400 focus:border-transparent transition duration-200"
                                                    >
                                                        <option value="en">English</option>
                                                        <option value="es">Español</option>
                                                        <option value="fr">Français</option>
                                                        <option value="de">Deutsch</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Date Format
                                                    </label>
                                                    <select className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-400 focus:border-transparent transition duration-200">
                                                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Data & Privacy */}
                                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Data & Privacy</h3>
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-white">Analytics & Usage Data</p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">Help improve our service by sharing anonymous usage data</p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input type="checkbox" defaultChecked className="sr-only peer" />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-400"></div>
                                                    </label>
                                                </div>
                                                <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                                                    <button className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-red-100 hover:bg-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded transition duration-200">
                                                        <i className="fas fa-download mr-2"></i>
                                                        Download My Data
                                                    </button>
                                                    <button onClick={() => setShowDeleteModal(true)} className="ml-3 inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-red-600 hover:bg-red-700 text-white rounded transition duration-200">
                                                        <i className="fas fa-trash mr-2"></i>
                                                        Delete Account
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Integrations Tab */}
                            {activeTab === 'integrations' && (
                                <div>
                                    <div className="mb-8">
                                        <h2 className="text-3xl font-semibold font-heading text-gray-900 dark:text-white mb-2">Connected Accounts</h2>
                                        <p className="text-gray-600 dark:text-gray-400">Enable or disable integrations with your cloud storage providers.</p>
                                    </div>

                                    <div className="space-y-4 max-w-xl">
                                        {[
                                            { key: 'google_drive', label: 'Google Drive', connectKey: 'google' },
                                            { key: 'dropbox', label: 'Dropbox', connectKey: 'dropbox' },
                                            { key: 'onedrive', label: 'OneDrive', connectKey: 'onedrive' },
                                        ].map(p => {
                                            const isComingSoon = p.key === 'dropbox' || p.key === 'onedrive';
                                            const isConnected = !!integrations[p.key];
                                            return (
                                                <div key={p.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-white">{p.label}</p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">{isComingSoon ? 'Coming soon' : (isConnected ? 'Connected' : 'Disconnected')}</p>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            disabled={integrationsLoading || isComingSoon}
                                                            onClick={async () => {
                                                                if (isComingSoon) return;
                                                                try {
                                                                    setIntegrationsLoading(true);
                                                                    if (isConnected) {
                                                                        await disconnectProvider(p.key);
                                                                        const st = await getIntegrationStatus();
                                                                        setIntegrations(st);
                                                                    } else {
                                                                        const { auth_url } = await getProviderAuthUrl(p.connectKey);
                                                                        if (auth_url) window.location.href = auth_url;
                                                                    }
                                                                } catch (e) {
                                                                    alert(e.message || 'Action failed');
                                                                } finally {
                                                                    setIntegrationsLoading(false);
                                                                }
                                                            }}
                                                            title={isComingSoon ? 'Coming soon' : undefined}
                                                            className={`px-4 py-2 text-sm rounded ${
                                                                isComingSoon
                                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                                    : (isConnected
                                                                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                                                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                                                            }`}
                                                        >
                                                            {isComingSoon ? 'Coming Soon' : (isConnected ? 'Disconnect' : 'Connect')}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/* Delete Account Modal */}
            <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Delete your account</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                This will {deleteAtPeriodEnd ? 'schedule deletion at the end of your current billing period' : 'immediately delete your account'}.
                Your subscription will be {deleteAtPeriodEnd ? 'set to cancel at period end' : 'cancelled immediately'}.
            </p>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <label className="text-sm text-gray-800 dark:text-gray-200">Delete at period end</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={deleteAtPeriodEnd} onChange={(e) => setDeleteAtPeriodEnd(e.target.checked)} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-400"></div>
                    </label>
                </div>
                <div className="flex items-center justify-between">
                    <label className="text-sm text-gray-800 dark:text-gray-200">Hard delete (staging)</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={hardDelete} onChange={(e) => setHardDelete(e.target.checked)} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-400"></div>
                    </label>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Reason (optional)</label>
                    <input
                        type="text"
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Tell us why you're leaving"
                    />
                </div>
            </div>
            <div className="mt-6 flex justify-end space-x-2">
                <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded">Cancel</button>
                <button onClick={confirmDeleteAccount} disabled={deleteLoading} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50">
                    {deleteLoading ? 'Deleting...' : (deleteAtPeriodEnd ? 'Schedule Deletion' : 'Delete Now')}
                </button>
            </div>
            </Modal>

            {/* Change Plan Modal */}
            <Modal open={showChangePlan} onClose={() => setShowChangePlan(false)}>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Change your plan</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Switch between plans and billing cycles. Prorations may apply.</p>
                {changePlanError && (
                    <div className="mb-3 p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">{changePlanError}</div>
                )}
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Plan</label>
                        <div className="flex items-center space-x-4">
                            <label className="inline-flex items-center space-x-2">
                                <input type="radio" name="planType" value="individual" checked={changePlanType === 'individual'} onChange={() => setChangePlanType('individual')} />
                                <span className="text-gray-800 dark:text-gray-200">Individual</span>
                            </label>
                            <label className="inline-flex items-center space-x-2">
                                <input type="radio" name="planType" value="family" checked={changePlanType === 'family'} onChange={() => setChangePlanType('family')} />
                                <span className="text-gray-800 dark:text-gray-200">Family</span>
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Billing cycle</label>
                        <div className="flex items-center space-x-4">
                            <label className="inline-flex items-center space-x-2">
                                <input type="radio" name="cycle" value="month" checked={changeBillingCycle === 'month'} onChange={() => setChangeBillingCycle('month')} />
                                <span className="text-gray-800 dark:text-gray-200">Monthly</span>
                            </label>
                            <label className="inline-flex items-center space-x-2">
                                <input type="radio" name="cycle" value="year" checked={changeBillingCycle === 'year'} onChange={() => setChangeBillingCycle('year')} />
                                <span className="text-gray-800 dark:text-gray-200">Annual</span>
                            </label>
                        </div>
                    </div>
                </div>
                <div className="mt-6 flex justify-end space-x-2">
                    <button onClick={() => setShowChangePlan(false)} className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded">Cancel</button>
                    <button onClick={submitChangePlan} disabled={changePlanLoading} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50">
                        {changePlanLoading ? 'Updating...' : 'Update Plan'}
                    </button>
                </div>
            </Modal>
        </div>
    );
}

// Simple modal styles inline for now
function Modal({ open, onClose, children }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
                {children}
            </div>
        </div>
    );
}
